import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Message, Conversation } from '@/types/chat';

export function useChat(bookingId: string, userType?: 'client' | 'practitioner') {
  const { user, activeRole } = useAuth();
  const { toast } = useToast();
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);

  // Determine sender type from userType prop or activeRole
  const senderType = userType || activeRole || 'client';

  // 获取或创建对话
  const getOrCreateConversation = useCallback(async () => {
    if (!user || !bookingId) return;

    try {
      // 先尝试获取现有对话 - 重试机制
      let existingConversation = null;
      let fetchError = null;
      let retries = 0;
      const maxRetries = 3;

      while (retries < maxRetries && !existingConversation) {
        const result = await supabase
          .from('conversations')
          .select('*')
          .eq('booking_id', bookingId)
          .maybeSingle();

        existingConversation = result.data;
        fetchError = result.error;

        if (existingConversation) {
          console.log('✅ Found existing conversation:', existingConversation.id);
          setConversation(existingConversation);
          return existingConversation;
        }

        if (fetchError && fetchError.code !== 'PGRST116') {
          // 真的有错误，不是"没找到"
          console.error('❌ Error fetching conversation:', fetchError);
          toast({
            title: "Error",
            description: "Failed to load conversation: " + (fetchError.message || 'Unknown error'),
            variant: "destructive",
          });
          return null;
        }

        retries++;
        if (retries < maxRetries) {
          // 等待后重试
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }

      // 对话不存在，创建新对话
      console.log('📝 Creating new conversation for booking:', bookingId);
      
      // 使用 upsert 来避免 duplicate key 错误
      const { data: newConversation, error: createError } = await supabase
        .from('conversations')
        .upsert(
          {
            booking_id: bookingId,
          },
          {
            onConflict: 'booking_id'
          }
        )
        .select()
        .single();

      if (createError) {
        console.error('❌ Error creating conversation:', createError);
        console.error('📋 Error details:', {
          code: createError.code,
          message: createError.message,
          details: createError.details,
          hint: createError.hint
        });
        
        // 如果是 duplicate key 错误，尝试获取已存在的对话
        if (createError.code === '23505' || createError.message?.includes('duplicate')) {
          console.log('📝 Duplicate key detected, trying to fetch existing conversation...');
          const { data: existingAfterError } = await supabase
            .from('conversations')
            .select('*')
            .eq('booking_id', bookingId)
            .maybeSingle();
          
          if (existingAfterError) {
            console.log('✅ Found conversation after duplicate error:', existingAfterError.id);
            setConversation(existingAfterError);
            return existingAfterError;
          }
        }
        
        toast({
          title: "Error",
          description: "Failed to create conversation: " + (createError.message || 'Unknown error'),
          variant: "destructive",
        });
        return null;
      }

      console.log('✅ Conversation created/retrieved successfully:', newConversation?.id);
      setConversation(newConversation);
      return newConversation;
    } catch (error) {
      console.error('❌ Unexpected error in getOrCreateConversation:', error);
      return null;
    }
  }, [user, bookingId, toast]);

  // Simplified loadMessages without auto-mark-as-read
  const loadMessages = useCallback(async (conversationId: string) => {
    try {
      console.log('🔄 Loading messages for conversation:', conversationId);
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('❌ Error loading messages:', error);
        toast({
          title: "Error",
          description: "Failed to load messages",
          variant: "destructive",
        });
        return;
      }

      const typedMessages = (data || []).map(msg => ({
        ...msg,
        sender_type: msg.sender_type as 'client' | 'practitioner'
      })) as Message[];

      console.log('✅ Loaded messages:', typedMessages.length);
      setMessages(typedMessages);
    } catch (error) {
      console.error('Unexpected error loading messages:', error);
    }
  }, [toast]);

  // 发送消息
  const sendMessage = useCallback(async (content: string) => {
    if (!user || !conversation || !content.trim()) return;

    setIsSending(true);
    try {
      const { error } = await supabase
        .from('messages')
        .insert({
          conversation_id: conversation.id,
          sender_id: user.id,
          sender_type: senderType,
          message_content: content.trim(),
          message_type: 'text',
          is_read: false,
        });

      if (error) {
        console.error('Error sending message:', error);
        toast({
          title: "Error",
          description: "Failed to send message",
          variant: "destructive",
        });
        return;
      }

      // After successfully sending the message, trigger email notification
      try {
        // Get booking details to find recipient
        const { data: booking, error: bookingError } = await supabase
          .from('bookings')
          .select(`
            id,
            client_id,
            practitioner_id,
            clients(first_name, last_name),
            practitioners(first_name, last_name)
          `)
          .eq('id', bookingId)
          .single();

        if (booking && !bookingError) {
          // Determine recipient (opposite of sender)
          const isClientSender = user.id === booking.client_id;
          const recipientId = isClientSender ? booking.practitioner_id : booking.client_id;
          const senderName = isClientSender 
            ? `${booking.clients?.first_name} ${booking.clients?.last_name}`.trim()
            : `${booking.practitioners?.first_name} ${booking.practitioners?.last_name}`.trim();
          const recipientName = isClientSender
            ? `${booking.practitioners?.first_name} ${booking.practitioners?.last_name}`.trim()
            : `${booking.clients?.first_name} ${booking.clients?.last_name}`.trim();

          // Get recipient email
          const { data: recipientAuth } = await supabase.auth.admin.getUserById(recipientId);
          
          if (recipientAuth.user?.email) {
            // Send email notification
            await supabase.functions.invoke('send-chat-notification', {
              body: {
                conversationId: conversation.id,
                senderName,
                messageContent: content.trim(),
                recipientEmail: recipientAuth.user.email,
                recipientName,
                bookingId
              }
            });
          }
        }
      } catch (emailError) {
        console.error('Error sending email notification:', emailError);
        // Don't show error to user as message was sent successfully
      }
    } catch (error) {
      console.error('Unexpected error sending message:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  }, [user, conversation, toast, bookingId, senderType]);

  // 标记消息为已读 - Enhanced with better error handling and logging
  const markMessagesAsRead = useCallback(async () => {
    if (!user || !conversation) return;

    try {
      console.log('📍 Marking messages as read for conversation:', conversation.id);
      
      const { error } = await supabase
        .from('messages')
        .update({ is_read: true })
        .eq('conversation_id', conversation.id)
        .neq('sender_id', user.id)
        .eq('is_read', false);

      if (error) {
        console.error('❌ Error marking messages as read:', error);
        return;
      }

      console.log('✅ Messages marked as read successfully');
      
      // Update local state to reflect read status
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg.sender_id !== user.id ? { ...msg, is_read: true } : msg
        )
      );
    } catch (error) {
      console.error('Unexpected error marking messages as read:', error);
    }
  }, [user, conversation]);

  // 初始化
  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);
      const conv = await getOrCreateConversation();
      if (conv) {
        await loadMessages(conv.id);
      }
      setIsLoading(false);
    };

    if (user && bookingId) {
      initialize();
    }
  }, [user, bookingId, getOrCreateConversation, loadMessages]);

  // 设置实时监听 - Enhanced with read status updates
  useEffect(() => {
    if (!conversation) return;

    console.log('🔌 Setting up realtime subscription for conversation:', conversation.id);

    const channel = supabase
      .channel(`messages-${conversation.id}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversation.id}`,
        },
        (payload) => {
          console.log('📨 Realtime message event:', payload);
          
          if (payload.eventType === 'INSERT') {
            const newMessage = {
              ...payload.new,
              sender_type: payload.new.sender_type as 'client' | 'practitioner'
            } as Message;
            setMessages(prev => [...prev, newMessage]);
          } else if (payload.eventType === 'UPDATE') {
            setMessages(prev => 
              prev.map(msg => 
                msg.id === payload.new.id ? { ...msg, ...payload.new } : msg
              )
            );
          }
        }
      )
      .subscribe((status) => {
        console.log('📡 Subscription status:', status);
      });

    return () => {
      console.log('🔌 Cleaning up realtime subscription');
      supabase.removeChannel(channel);
    };
  }, [conversation]);

  return {
    messages,
    conversation,
    isLoading,
    isSending,
    sendMessage,
    markMessagesAsRead,
  };
}
